<template>
  <div
    v-show="shouldShow"
    :class="['phoenix-keyboard-wrapper', `keyboard-${keyboardType}`]"
    id="phoenix-keyboard-wrapper"
  >
    <div :class="['ExternalKeyboard', { outline: showOutline, disabled: keyboardLocked }]">
      <div v-for="row in displayKeys" :key="row.name" :class="[`row ${row.name}`, theme]">
        <component
          :is="getKwtKey(key)"
          v-for="key in row.keys"
          :key="key.name"
          :name="key.name"
          :character="key.character"
          :alt="key.alt"
          :shift="key.shift"
          :hold-hand="key.hold"
          :row="row.name"
        ></component>
      </div>
    </div>
  </div>
</template>

<script>
import BaseKeyboard from 'mixins/BaseKeyboard';
import { keyLayout } from 'constants/standard-keyboard';
import HoldKey from './keys/TouchHoldKey.vue';
import { createNamespacedHelpers } from 'vuex';
import StandardKey from './keys/StandardKey.vue';

const keyboard = createNamespacedHelpers('keyboard');
const keyboardName = 'ExternalKeyboard';
const keys = {
  standard: 'standard',
  hold: 'hold'
};

export default {
  name: keyboardName,

  components: {
    [keys.standard]: StandardKey,
    [keys.hold]: HoldKey
  },

  mixins: [BaseKeyboard],

  props: {
    theme: {
      default: 'light',
      validator: theme => ['light', 'dark'].includes(theme)
    }
  },

  computed: {
    displayKeys() {
      if (!this.hideNumberRow) return this.keyLayout;
      return this.keyLayout.filter(row => row.name !== 'rowNumber');
    }
  },

  data() {
    return {
      keyboardName,
      processKeyPress: null,
      kwtKey: '',
      keyLayout
    };
  },

  created() {
    if (this.isTouchHold) {
      this.processKeyPress = this.processTouchHoldKey;
      return;
    }

    this.processKeyPress = this.processStandardKey;
    this.$root.$refs.__ExternalKeyboard__ = this;
  },

  beforeDestroy() {
    delete this.$root.$refs.__ExternalKeyboard__;
  },

  methods: {
    ...keyboard.mapMutations(['setCapsLock', 'toggleSpecialKeys']),

    addKeyListeners() {
      window.addEventListener('keydown', this.handleKeyDown);
      window.addEventListener('keyup', this.handleKeyUp);

      if (this.isTouchHold) {
        window.addEventListener('keydown', this.handleHoldKeyDown);
        window.addEventListener('keyup', this.handleHoldKeyUp);
      }
    },

    removeKeyListeners() {
      window.removeEventListener('keydown', this.handleKeyDown);
      window.removeEventListener('keydown', this.handleHoldKeyDown);

      window.removeEventListener('keyup', this.handleKeyUp);
      window.removeEventListener('keyup', this.handleHoldKeyUp);
    },

    getKwtKey(key) {
      if (this.isOneHandedTypingHandActivity) {
        return keys.standard;
      }

      if (this.isTouchHold && key.hold && this.targetKey === key.name) {
        return keys.hold;
      }

      return keys.standard;
    },

    processTouchHoldKey(key, { withShift }) {
      if (this.keyboardLocked && (this.isOneHandedTypingHandActivity || !this.capsLock)) return;
      this.attemptKey({ key, withShift });
    },

    processStandardKey(key, { withCmd, withShift }) {
      this.attemptKey({ key, withCmd, withShift });
    },

    doNotHandleKey(event) {
      const { key } = event;
      const withShiftKey = event.shiftKey;

      return (
        (key !== 'Backspace' &&
          (this.keyboardDisabled ||
            key === 'CapLock' ||
            this.keyNotUsedInActivity(key, event) ||
            this.keyIsPressed({ event }))) ||
        (key === 'Tab' && withShiftKey)
      );
    },

    handleKeyDown(event) {
      const { location = '' } = event;
      let key = event.key || event;

      if (event.preventDefault) {
        event.preventDefault();
      }

      if (this.doNotHandleKey(event)) return;

      if (this.targetKey.includes('shift') && this.holdKeyPressed) {
        key = this.modifyWithShift(key);
      }

      const keysList = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(i => i.toLowerCase());
      if (!event.shiftKey && keysList.indexOf(key.toLowerCase()) !== -1) {
        this.setCapsLock(key === key.toUpperCase());
      }

      const normalizedKey = this.normalizeKey(key, location);
      this.processKeyPress(normalizedKey, { withCmd: event.metaKey, withShift: event.shiftKey });

      // don't add shift to activeKey array. shift is a 'free' key
      if (!this.isShiftKey(key)) {
        if (
          this.attemptedKey?.isCorrect &&
          this.keyCodesToNotHandleNatively.includes(event.keyCode)
        ) {
          event.preventDefault();
        }
        this.addToActiveKeys({ event });
      }
    },

    /*
       iOS Safari does not register keyup events. Thus, we don't check the key on keyup.
       In the future, we may find that other mobile platforms may include key event data on keyup
       events, in which case we may need/want to separate iOS vs say Android or MS Surface.
       Hopefully not though, and this will suffice.
     */
    handleKeyUp(event) {
      event.preventDefault();
      this.clearActiveKeys();
      if (this.keyboardLocked) return;
      this.resetAttemptedKey();
    },

    handleHoldKeyDown(event) {
      event.preventDefault();

      if (this.keyNotUsedInActivity(event.key, event)) return;

      if (
        (event.key === this.targetKey || this.isShiftKeyTarget(event.key)) &&
        this.keyboardLocked
      ) {
        this.pressHoldKey();
        this.addToActiveKeys({ event });
      }
    },

    handleHoldKeyUp(event) {
      event.preventDefault();

      if (event.key === this.targetKey || this.isShiftKeyTarget(event.key)) {
        this.releaseHoldKey();
        this.removeFromActiveKeys({ event });
      }
    },

    isShiftKeyTarget(key) {
      return this.targetKey.includes('shift') && this.isShiftKey(key);
    },

    isShiftKey(keyPressed) {
      const key = keyPressed.toLowerCase();
      // iOS registers Shift as 'Dead'
      return key.includes('shift') || key === 'dead';
    },

    normalizeKey(key, location = '') {
      const normalized = {
        enter: 'enter',
        spacebar: ' ',
        quotationmark: '"',
        shift: `shift${location}`,
        tab: 'tab'
      };

      return normalized[key.toLowerCase()] || key;
    },

    modifyWithShift(key) {
      const specialKeys = {
        ';': ':',
        ',': '!',
        '.': '?'
      };

      const isLetter = key.length === 1 && /[a-z]/.test(key.toLowerCase());
      return isLetter ? key.toUpperCase() : specialKeys[key];
    }
  }
};
</script>
